import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useMemo } from 'react';
import type { ObjectMetadata } from '../../data/client';
import { usePreferences, useSetPreference } from '../../data/hooks';
import type { ObjectNavGroup } from './objectGroups';

/**
 * WHICH OBJECT ROWS THIS PERSON KEEPS IN THE SIDEBAR, AND WHICH GROUPS THIS BROWSER HAS FOLDED
 * (fulfillment wave, package P2, items 3 and 4).
 *
 * Apple's model for the first is Finder's: a sidebar item is hidden by the person, on the row
 * itself or from a list of checkboxes in Settings, and hiding it removes nothing but the row.
 * support: https://support.apple.com/guide/mac-help/customize-the-finder-sidebar-on-mac-mchl83c9e8b8/mac
 * - "Choose Finder > Settings, click Sidebar, then select or deselect items" and "The link to
 * the item in the sidebar disappears, but the folder, disk, or file remains in its original
 * location on your Mac".
 * So VISIBILITY IS NOT PERMISSION here either: a hidden object's route, its search results and
 * every link to it keep working, and only `ObjectNavList` reads this list.
 *
 * THE KEY IS `sidebar.hiddenobjects`, NOT `sidebar.hiddenObjects`. The contract named the
 * camel-cased key, and the server refuses it: a preference key is "dotted lower-case segments"
 * (`packages/shared/src/auth.ts`, `userPreferenceKeySchema`), so a PUT with a capital letter
 * is a 400 on every real installation while the fixture client, which validates nothing,
 * would have stored it happily. Ledger FX-P2-1.
 */
export const SIDEBAR_HIDDEN_OBJECTS_KEY = 'sidebar.hiddenobjects';

/** The person's group folds, per browser (the contract's own key; localStorage, not a preference). */
export const SIDEBAR_COLLAPSED_GROUPS_KEY = 'sidebar.collapsedGroups';

/** A stored value that is not a list of strings is treated as "nothing hidden", never as a crash. */
export function readHiddenObjects(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * The sidebar's groups with the hidden objects taken out. A group whose members are all
 * hidden disappears with its heading, because a heading over nothing is the thing
 * `ObjectNavList` already refuses to draw for an empty list.
 */
export function withoutHidden(
  groups: readonly ObjectNavGroup[],
  hidden: readonly string[],
): readonly ObjectNavGroup[] {
  if (hidden.length === 0) return groups;
  const set = new Set(hidden);
  return groups
    .map((group) => ({
      ...group,
      objects: group.objects.filter((object) => !set.has(object.name)),
    }))
    .filter((group) => group.objects.length > 0);
}

/** Adds or removes one api name, keeping the list free of duplicates and in a stable order. */
export function setObjectHidden(
  hidden: readonly string[],
  name: string,
  isHidden: boolean,
): readonly string[] {
  const rest = hidden.filter((entry) => entry !== name);
  return isHidden ? [...rest, name] : rest;
}

/**
 * WHETHER A GROUP IS OPEN, AND WHO DECIDED IT (redesign register A5-4: "closed by default for Ben
 * and the coaches (per role), a group opens itself when the current page is one of its lists;
 * per-person override kept").
 *
 * Three answers, strongest first:
 * 1. The group whose list is the current page is open (R34, "where am I"), unless the person
 *    closed it on this very page: a self-opened group still closes when its heading is pressed.
 *    Opening itself writes nothing, so the person's own choice is there again on the next page.
 * 2. The person's own choice in this browser, open or closed.
 * 3. The role's default: closed for a person holding the seeded Owner or Coach access role, open
 *    for everybody else (coordinator decision Q-NAV-1).
 */
export type GroupFold = 'open' | 'closed';

export type GroupFolds = Readonly<Record<string, GroupFold>>;

/** The access roles whose groups start closed (Q-NAV-1), matched by the seeded label. */
export const CLOSED_GROUP_ROLES: readonly string[] = ['Owner', 'Coach'];

export function roleFoldDefault(
  roles: readonly { readonly label: string }[] | undefined,
): GroupFold {
  return roles?.some((role) => CLOSED_GROUP_ROLES.includes(role.label)) === true
    ? 'closed'
    : 'open';
}

/**
 * What this browser holds under `sidebar.collapsedGroups`. A map of label to fold is today's
 * shape; a LIST of labels is the shape before 2026-10-04, when a choice could only fold, and each
 * label in it reads as closed. Anything else reads as no choice at all.
 */
export function readGroupFolds(value: unknown): GroupFolds {
  if (Array.isArray(value)) {
    return Object.fromEntries(
      value
        .filter((entry): entry is string => typeof entry === 'string')
        .map((label) => [label, 'closed']),
    );
  }
  if (value === null || typeof value !== 'object') return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, GroupFold] => entry[1] === 'open' || entry[1] === 'closed',
    ),
  );
}

export function isGroupOpen(input: {
  readonly stored: GroupFold | undefined;
  readonly roleDefault: GroupFold;
  /** The current page is one of this group's lists. */
  readonly current: boolean;
  /** The person closed this group while standing on the current page. */
  readonly closedHere: boolean;
}): boolean {
  if (input.current && !input.closedHere) return true;
  return (input.stored ?? input.roleDefault) === 'open';
}

/** The person's choice for one group, written beside the others. */
export function withGroupFold(folds: GroupFolds, label: string, open: boolean): GroupFolds {
  return { ...folds, [label]: open ? 'open' : 'closed' };
}

export interface SidebarHiddenObjects {
  readonly hidden: readonly string[];
  readonly setHidden: (next: readonly string[]) => void;
  readonly hide: (object: ObjectMetadata) => void;
}

export function useSidebarHiddenObjects(): SidebarHiddenObjects {
  const preferences = usePreferences();
  const setPreference = useSetPreference();
  const hidden = useMemo(
    () => readHiddenObjects(preferences.data?.[SIDEBAR_HIDDEN_OBJECTS_KEY]),
    [preferences.data],
  );
  const setHidden = (next: readonly string[]): void => {
    setPreference.mutate({ key: SIDEBAR_HIDDEN_OBJECTS_KEY, value: next });
  };
  return {
    hidden,
    setHidden,
    hide: (object) => setHidden(setObjectHidden(hidden, object.name, true)),
  };
}

/**
 * Per browser, like the sidebar's width (`shellState.ts`): folding a group is a preference about
 * looking, and the brief puts it in localStorage. `getOnInit` so a reload paints the folded state
 * on the first frame instead of opening and then snapping shut.
 */
const groupFoldsAtom = atomWithStorage<unknown>(SIDEBAR_COLLAPSED_GROUPS_KEY, {}, undefined, {
  getOnInit: true,
});

export function useGroupFolds(): {
  readonly folds: GroupFolds;
  readonly setFold: (label: string, open: boolean) => void;
} {
  const [stored, setStored] = useAtom(groupFoldsAtom);
  const folds = readGroupFolds(stored);
  return { folds, setFold: (label, open) => setStored(withGroupFold(folds, label, open)) };
}
